import { Directive, Input, OnChanges, OnInit, inject } from '@angular/core';
import { Store } from '@df/catalog/store/store';
import { AbstractGoogleMaps } from '@df/module-google-maps/abstract-google-maps.component';
import { GoogleMaps } from '@df/module-google-maps/google-maps.namespace';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import extend from 'lodash-es/extend';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';

export interface IMarkerOptions extends google.maps.MarkerOptions {
    info?: string;
}

@Directive()
export abstract class GoogleMapComponentDef extends AbstractGoogleMaps implements OnInit, OnChanges {
    /**
     * Google map options
     * @type {google.maps.MapOptions | undefined}
     */
    @Input()
    options?: google.maps.MapOptions;

    /**
     * Markers to put on map
     * You can use markers or stores
     * @type {IMarkerOptions | undefined}
     */
    @Input()
    markers?: GoogleMaps.IMarkerOptions[];

    /**
     * Stores to be converted to markers
     * @type {Store[] | undefined}
     */
    @Input()
    stores?: Store[];

    /**
     * Max number of markers to show
     * In stores mode we will sort by distance first
     * @type {number | undefined}
     */
    @Input()
    markerLimit?: number;

    /**
     * Component option
     * If true then on every markers set zoom will adjust to show all of them
     * @type {boolean}
     */
    @Input()
    fitBounds = false;

    /**
     * If true and there is only one marker, InfoWindow will be opened
     * @type {boolean}
     */
    @Input()
    openInfoWindow = false;

    /**
     * Max zoom allowed
     * defaults to zoom from options
     */
    @Input()
    maxZoom?: number;

    /**
     * Center first marker on map,
     * needed if you have only one marker and and not using fitBounds
     */
    @Input()
    centerFirstMarker?: boolean;

    /**
     * Info window instance
     * @type {google.maps.InfoWindow}
     */
    protected infoWindow?: google.maps.InfoWindow;

    /**
     * Markers placed on google maps
     */
    protected gmapMarkers: { [hash: string]: google.maps.Marker } = {};

    /**
     * Original zoom
     */
    protected zoom?: number;

    /**
     * Original map edges
     */
    protected bounds: any;

    private moduleConfig = inject(GoogleMaps.OPTIONS);

    protected mapOptions: google.maps.MapOptions = this.moduleConfig.mapOptions;

    protected setMarkersOnMap = this.timeoutService.debounce(() => {
        const googleMap = this.googleMap;

        if (!googleMap) {
            return;
        }

        // prepare hashed index of markers
        const markersIndex: {
            [key: string]: GoogleMaps.IMarkerOptions;
        } = {};

        let count = 0;
        each(this.markers, (marker: GoogleMaps.IMarkerOptions) => {
            if (this.markerLimit === undefined || count < this.markerLimit) {
                const hash = this.markerHash(marker);
                if (hash) {
                    if (!markersIndex[hash]) {
                        count++;
                    }
                    markersIndex[hash] = marker;
                }
            }
        });

        // remove any markers existing on map that should no longer be printed
        each(this.gmapMarkers, (gmapMarker, hash) => {
            if (!markersIndex[hash]) {
                gmapMarker.setMap(null);
            }
        });

        // add new markers
        each(markersIndex, (marker, hash) => {
            const gmapMarker = this.gmapMarkers[hash];
            const getMarkerSetup = (): GoogleMaps.IMarkerOptions => {
                return extend(
                    {},
                    {
                        map: googleMap,
                        animation: this.googleMapApi.Animation.DROP,
                        draggable: false
                    },
                    marker
                );
            };
            if (!gmapMarker) {
                const markerSetup = getMarkerSetup();

                const newGmapMarker = new this.googleMapApi.Marker(markerSetup);

                this.googleMapApi.event.addListener(newGmapMarker, 'click', () => {
                    this.onMarkerClick(markerSetup, newGmapMarker);
                });
                this.gmapMarkers[hash] = newGmapMarker;
            } else {
                gmapMarker.setMap(googleMap);
            }

            if (this.openInfoWindow && this.markers && this.infoWindow && this.markers.length === 1) {
                const markerSetup = getMarkerSetup();
                const gmapMarker = this.gmapMarkers[hash];
                if (gmapMarker && markerSetup) {
                    this.onMarkerClick(markerSetup, gmapMarker);
                }
            }

            if (this.fitBounds) {
                const currentZoom = googleMap.getZoom();
                if (!currentZoom) {
                    googleMap.setZoom(this.maxZoom || 20);
                }
                const bounds: google.maps.LatLngBounds = new this.googleMapApi.LatLngBounds();
                each(markersIndex, marker => {
                    if (marker.position) {
                        bounds.extend(marker.position);
                    }
                });
                googleMap.fitBounds(bounds);
                this.applyMaxZoom();
                this.onFitBounds();
            }
        });
    }, 100);

    private applyMaxZoom() {
        const maxZoom = this.maxZoom || this.mapOptions.zoom;
        if (maxZoom && this.googleMap) {
            const currentZoom = this.googleMap.getZoom();
            this.googleMap.setZoom(currentZoom ? Math.min(currentZoom, maxZoom) : maxZoom);
        }
    }

    onInit() {
        this.googleMap = new this.googleMapApi.Map(this.element.nativeElement, this.mapOptions);
        this.infoWindow = new this.googleMapApi.InfoWindow(this.getInfoWindowOptions());
        if (this.stores) {
            this.onStoresChange();
        } else {
            this.setMarkersOnMap();
        }
        if (this.mapOptions.zoom) {
            this.googleMap!.setZoom(this.mapOptions.zoom);
        }
        this.centerFirstMarkerOnMap();
    }

    ngOnChanges(changes) {
        if (changes.options) {
            this.mapOptions = this.options ? { ...this.moduleConfig.mapOptions, ...this.options } : this.moduleConfig.mapOptions;
        }
        if (this.initiated) {
            if (changes.options) {
                this.googleMap?.setOptions(this.mapOptions);
            }
            if (changes.markers || changes.markerLimit) {
                this.setMarkersOnMap();
            } else if (changes.maxZoom) {
                this.applyMaxZoom();
            }
            if (changes.stores) {
                this.onStoresChange();
            }
            if (changes.centerFirstMarker) {
                this.centerFirstMarkerOnMap();
            }
        }
    }

    onStoresChange(): void {
        if (this.stores) {
            const localConverter = this.getConverter();
            this.setMarkers(
                compact(
                    map(sortBy(this.stores, 'distance'), store => {
                        const options = this.getMarkerOptions(store);

                        if (!options) {
                            return undefined;
                        }
                        if (localConverter) {
                            return localConverter(store, options);
                        } else {
                            return options;
                        }
                    })
                )
            );
        }
    }

    private setMarkers(markers?: GoogleMaps.IMarkerOptions[]) {
        this.markers = markers;
        this.setMarkersOnMap();
    }

    private centerFirstMarkerOnMap() {
        if (this.centerFirstMarker && !this.fitBounds && this.markers && this.markers.length) {
            const marker = this.markers[0];
            if (marker.position && this.googleMap) {
                this.googleMap.setCenter(marker.position);
            }
        }
    }

    protected getConverter(): GoogleMaps.MarkerConverter | undefined {
        return this.moduleConfig.storeToMarker;
    }

    protected getMarkerOptions(store: Store): GoogleMaps.IMarkerOptions | undefined {
        if (store.data && store.data.latitude && store.data.longitude) {
            return {
                title: store.data.store_name,
                position: {
                    lat: store.data.latitude,
                    lng: store.data.longitude
                },
                ...(store.id && { storeId: store.id })
            };
        }
        return undefined;
    }

    /**
     * Returns hash for marker
     * @param {IMarkerOptions} marker - marker model
     * @returns {string | undefined}
     */
    protected markerHash(marker: GoogleMaps.IMarkerOptions): string | undefined {
        return [marker.position ? marker.position.lat + 'x' + marker.position.lng : undefined, marker.info].join('|');
    }

    /**
     * Get initial map settings
     */
    protected onFitBounds() {
        this.bounds = this?.googleMap?.getBounds();
        this.zoom = this?.googleMap?.getZoom();
    }

    protected onMarkerClick(markerSetup: GoogleMaps.IMarkerOptions, marker: google.maps.Marker): void {
        if (this.infoWindow) {
            this.infoWindow.close();
            this.infoWindow.setContent(this.getInfoWindowContent(markerSetup));
            this.infoWindow.open(this.googleMap, marker);
        }
    }

    protected getInfoWindowOptions(): google.maps.InfoWindowOptions | undefined {
        return undefined;
    }

    protected getInfoWindowContent(markerSetup: GoogleMaps.IMarkerOptions): string | undefined {
        return markerSetup.info;
    }

    /**
     * Restore map settings from configuration
     */
    resetMap() {
        if (this.googleMap) {
            this.googleMap.fitBounds(this.bounds);
            if (this.zoom) {
                this.googleMap.setZoom(this.zoom);
            }
        }
    }
}
